import { memo, useState } from 'react'

import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { AddCircleOutlineOutlined, Videocam } from '@mui/icons-material'
import { Box, Button, Divider, Paper, Stack, Typography } from '@mui/material'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'

import { newId } from '../ids'
import { StreamPickerDialogContent } from '../StreamPickerDialogContent'
import type { ConfigFormValues } from '../types'
import { ViewRow } from './ViewRow'

export type PanelProps = {
  repositoryId?: string
}

const PanelRaw = ({ repositoryId }: PanelProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const [pickerOpen, setPickerOpen] = useState(false)

  const { control } = useFormContext<ConfigFormValues>()
  const { fields, append, remove, move } = useFieldArray({ control, name: 'viewRows', keyName: 'fieldId' })

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) {
      const oldIndex = fields.findIndex((field) => field.fieldId === active.id)
      const newIndex = fields.findIndex((field) => field.fieldId === over.id)
      if (oldIndex !== -1 && newIndex !== -1) {
        move(oldIndex, newIndex)
      }
    }
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="heading-md">{t('domain:Workspace.Event.ConfigEditor.cameras')}</Typography>
        <Button
          size="small"
          startIcon={<AddCircleOutlineOutlined />}
          disabled={!repositoryId}
          onClick={() => setPickerOpen(true)}
        >
          {t('domain:Workspace.Event.ConfigEditor.addCamera')}
        </Button>
      </Box>
      {!fields.length ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4, color: 'text.secondary' }}>
          <Videocam />
          <Typography variant="heading-base">{t('domain:Workspace.Event.ConfigEditor.noCameras')}</Typography>
        </Stack>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext items={fields.map((field) => field.fieldId)} strategy={verticalListSortingStrategy}>
            <Stack divider={<Divider flexItem />}>
              {fields.map((field, index) => (
                <ViewRow
                  key={field.fieldId}
                  id={field.fieldId}
                  index={index}
                  repositoryId={repositoryId}
                  streamEditable={field.streamEditable}
                  onRemove={() => remove(index)}
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}

      {/* The stream comes first: a camera without one serializes to a track with no source. */}
      <Dialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        title={t('domain:Workspace.Event.ConfigEditor.selectVideoStreamTitle')}
      >
        <StreamPickerDialogContent
          repositoryId={repositoryId}
          kind="video"
          onSelect={(stream) =>
            append({
              entityId: newId(),
              name: '',
              enabled: true,
              trackId: newId(),
              streamId: stream.id,
              streamEditable: true,
            })
          }
        />
      </Dialog>
    </Paper>
  )
}

export const Panel = memo(PanelRaw)
